// gym/GymPayments.jsx - VERSION CORRIGÉE
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, Alert, Snackbar, Chip, 
  FormControl, InputLabel, Select, MenuItem,
  Tooltip, Card, CardContent,
  Tab, Tabs
} from '@mui/material';
import { 
  Add, Edit, Delete, AttachMoney, Search, Refresh, CalendarToday,
  Person, Receipt, Paid, Pending, CreditCard,
  Print
} from '@mui/icons-material';
import api from '../../src/services/auth';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const GymPayments = () => {
  const [payments, setPayments] = useState([]);
  const [clients, setClients] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    client: '',
    membership: '',
    amount_usd: '',
    amount_htg: '',  // CHAMP OBLIGATOIRE
    payment_method: 'CASH',  // EN MAJUSCULES
    status: 'PAID',  // EN MAJUSCULES
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    notes: '',
    receipt_number: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(true);

  const paymentMethods = [
    { value: 'CASH', label: 'Espèces', icon: <AttachMoney /> },
    { value: 'CARD', label: 'Carte bancaire', icon: <CreditCard /> },
    { value: 'CHECK', label: 'Chèque', icon: <Receipt /> },
    { value: 'TRANSFER', label: 'Virement', icon: <Paid /> },
    { value: 'MOBILE', label: 'Mobile Money', icon: <Paid /> },
    { value: 'CREDIT', label: 'Crédit', icon: <CreditCard /> },
    { value: 'OTHER', label: 'Autre', icon: <AttachMoney /> }
  ];

  const paymentStatuses = [
    { value: 'PAID', label: 'Payé', color: 'success' },
    { value: 'PENDING', label: 'En attente', color: 'warning' },
    { value: 'FAILED', label: 'Échoué', color: 'error' },
    { value: 'REFUNDED', label: 'Remboursé', color: 'info' },
    { value: 'PARTIAL', label: 'Partiel', color: 'info' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterPayments();
  }, [payments, searchTerm, statusFilter, paymentMethodFilter, tabValue]);

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('Fetching payments data...');
      
      const [paymentsRes, clientsRes, membershipsRes] = await Promise.all([
        api.get('/gym/payments/'),
        api.get('/gym/clients/'),
        api.get('/gym/memberships/')
      ]);
      
      console.log('Payments API response:', paymentsRes);
      console.log('Payments data:', paymentsRes.data);
      
      // Essayons différentes structures de réponse
      let paymentsData = [];
      if (Array.isArray(paymentsRes.data)) {
        paymentsData = paymentsRes.data;
      } else if (paymentsRes.data && Array.isArray(paymentsRes.data.results)) {
        paymentsData = paymentsRes.data.results;
      } else if (paymentsRes.data && paymentsRes.data.data && Array.isArray(paymentsRes.data.data)) {
        paymentsData = paymentsRes.data.data;
      }
      
      console.log('Processed payments:', paymentsData);
      console.log('First payment:', paymentsData[0]);
      
      let clientsData = [];
      if (Array.isArray(clientsRes.data)) {
        clientsData = clientsRes.data;
      } else if (clientsRes.data && Array.isArray(clientsRes.data.results)) {
        clientsData = clientsRes.data.results;
      }
      
      let membershipsData = [];
      if (Array.isArray(membershipsRes.data)) {
        membershipsData = membershipsRes.data;
      } else if (membershipsRes.data && Array.isArray(membershipsRes.data.results)) {
        membershipsData = membershipsRes.data.results;
      }
      
      setPayments(paymentsData);
      setClients(clientsData);
      setMemberships(membershipsData);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      console.error('Error response:', error.response);
      setSnackbar({ 
        open: true, 
        message: `Erreur lors du chargement: ${error.message}`, 
        severity: 'error' 
      });
      setPayments([]);
      setClients([]);
      setMemberships([]);
    } finally {
      setLoading(false);
    }
  };

  const filterPayments = () => {
    let filtered = Array.isArray(payments) ? payments : [];
    
    if (searchTerm) {
      filtered = filtered.filter(payment =>
        payment?.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment?.receipt_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment?.membership_type?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(payment => payment?.status === statusFilter);
    }
    
    if (paymentMethodFilter !== 'all') {
      filtered = filtered.filter(payment => payment?.payment_method === paymentMethodFilter);
    }
    
    const today = new Date();
    const thisMonth = today.getMonth();
    const thisYear = today.getFullYear();
    
    switch (tabValue) {
      case 1: // Aujourd'hui
        filtered = filtered.filter(payment => {
          if (!payment?.payment_date) return false;
          const paymentDate = new Date(payment.payment_date);
          return paymentDate.toDateString() === today.toDateString();
        });
        break;
      case 2: // Ce mois
        filtered = filtered.filter(payment => {
          if (!payment?.payment_date) return false;
          const paymentDate = new Date(payment.payment_date);
          return paymentDate.getMonth() === thisMonth && 
                 paymentDate.getFullYear() === thisYear;
        });
        break;
      case 3: // En attente
        filtered = filtered.filter(payment => payment?.status === 'PENDING');
        break;
      default:
        break;
    }
    
    setFilteredPayments(filtered);
  };

  const calculateStats = () => {
    if (!Array.isArray(payments)) {
      return { totalAmountHTG: 0, pendingAmountHTG: 0, todayAmountHTG: 0, totalAmountUSD: 0 };
    }
    
    const totalAmountHTG = payments
      .filter(p => p && p.status === 'PAID')
      .reduce((sum, p) => sum + parseFloat(p.amount_htg || 0), 0);
    
    const totalAmountUSD = payments
      .filter(p => p && p.status === 'PAID')
      .reduce((sum, p) => sum + parseFloat(p.amount_usd || 0), 0);
    
    const pendingAmountHTG = payments
      .filter(p => p && p.status === 'PENDING')
      .reduce((sum, p) => sum + parseFloat(p.amount_htg || 0), 0);
    
    const todayAmountHTG = payments
      .filter(p => {
        if (!p) return false;
        const paymentDate = new Date(p.payment_date);
        const today = new Date();
        return paymentDate.toDateString() === today.toDateString() && p.status === 'PAID';
      })
      .reduce((sum, p) => sum + parseFloat(p.amount_htg || 0), 0);
    
    return { totalAmountHTG, totalAmountUSD, pendingAmountHTG, todayAmountHTG };
  };

  const stats = calculateStats();

  const handleOpenDialog = (payment = null) => {
    if (payment) {
      console.log('Editing payment:', payment);
      setEditingPayment(payment);
      setFormData({
        client: payment.client || payment.client_id || '',
        membership: payment.membership || payment.membership_id || '',
        amount_usd: payment.amount_usd || '',
        amount_htg: payment.amount_htg || '',
        payment_method: payment.payment_method || 'CASH',
        status: payment.status || 'PAID',
        payment_date: payment.payment_date ? format(new Date(payment.payment_date), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
        notes: payment.notes || '',
        receipt_number: payment.receipt_number || ''
      });
    } else {
      setEditingPayment(null);
      setFormData({
        client: '',
        membership: '',
        amount_usd: '',
        amount_htg: '',
        payment_method: 'CASH',
        status: 'PAID',
        payment_date: format(new Date(), 'yyyy-MM-dd'),
        notes: '',
        receipt_number: `REC-${Date.now().toString().slice(-6)}`
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPayment(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.client) {
      setSnackbar({ open: true, message: 'Veuillez sélectionner un client', severity: 'error' });
      return;
    }
    
    if (!formData.amount_htg) {
      setSnackbar({ open: true, message: 'Veuillez renseigner le montant en HTG', severity: 'error' });
      return;
    }
    
    // Préparer les données EXACTEMENT comme l'API les attend
    const submitData = {
      client: formData.client,
      membership: formData.membership || null,
      amount_usd: parseFloat(formData.amount_usd) || 0,
      amount_htg: parseFloat(formData.amount_htg), // OBLIGATOIRE
      payment_method: formData.payment_method, // Doit être en MAJUSCULES
      status: formData.status, // Doit être en MAJUSCULES
      payment_date: formData.payment_date,
      notes: formData.notes || '',
      receipt_number: formData.receipt_number || ''
    };

    console.log('Submitting payment data:', submitData);

    try {
      if (editingPayment) {
        await api.put(`/gym/payments/${editingPayment.id}/`, submitData);
        setSnackbar({ open: true, message: 'Paiement modifié avec succès', severity: 'success' });
      } else {
        await api.post('/gym/payments/', submitData);
        setSnackbar({ open: true, message: 'Paiement créé avec succès', severity: 'success' });
      }
      fetchData();
      handleCloseDialog();
    } catch (error) {
      console.error('Submit error details:', error);
      console.error('Error response data:', error.response?.data);
      
      let errorMessage = 'Erreur lors de l\'opération';
      if (error.response?.data) {
        if (typeof error.response.data === 'object') {
          errorMessage = Object.entries(error.response.data)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join(' | ');
        } else {
          errorMessage = String(error.response.data);
        }
      }
      
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: 'error' 
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce paiement?')) {
      try {
        await api.delete(`/gym/payments/${id}/`);
        setSnackbar({ open: true, message: 'Paiement supprimé avec succès', severity: 'success' });
        fetchData();
      } catch (error) {
        setSnackbar({ open: true, message: 'Erreur lors de la suppression', severity: 'error' });
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy HH:mm', { locale: fr });
    } catch (error) {
      try {
        return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: fr });
      } catch (e) {
        return dateString;
      }
    }
  };

  const getPaymentMethodIcon = (method) => {
    const found = paymentMethods.find(m => m.value === method);
    return found ? found.icon : <AttachMoney />;
  };

  const getPaymentMethodLabel = (method) => {
    const found = paymentMethods.find(m => m.value === method);
    return found ? found.label : method;
  };

  const getStatusInfo = (status) => {
    const found = paymentStatuses.find(s => s.value === status);
    return found || { label: status, color: 'default' };
  };

  const getClientName = (payment) => {
    if (payment.client_name) return payment.client_name;
    if (payment.client_details?.first_name) {
      return `${payment.client_details.first_name} ${payment.client_details.last_name || ''}`;
    }
    if (payment.client?.first_name) {
      return `${payment.client.first_name} ${payment.client.last_name || ''}`;
    }
    return 'Client inconnu';
  };

  const getMembershipType = (payment) => {
    if (payment.membership_type) return payment.membership_type;
    if (payment.membership?.membership_type) return payment.membership.membership_type;
    return 'N/A';
  };

  return (
    <Box p={3}>
      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <AttachMoney color="primary" />
                <Typography variant="h6">Total HTG</Typography>
              </Box>
              <Typography variant="h4" color="primary.main">
                {stats.totalAmountHTG.toLocaleString()} G
              </Typography>
              <Typography variant="body2" color="text.secondary">
                USD: {stats.totalAmountUSD.toFixed(2)} $
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ backgroundColor: 'warning.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <Pending />
                <Typography variant="h6">En Attente</Typography>
              </Box>
              <Typography variant="h4">
                {stats.pendingAmountHTG.toLocaleString()} G
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ backgroundColor: 'success.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <CalendarToday />
                <Typography variant="h6">Aujourd'hui</Typography>
              </Box>
              <Typography variant="h4">
                {stats.todayAmountHTG.toLocaleString()} G
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card sx={{ backgroundColor: 'info.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <Receipt />
                <Typography variant="h6">Transactions</Typography>
              </Box>
              <Typography variant="h4">
                {payments.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* En-tête */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">💰 Paiements GYM</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Nouveau Paiement
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Tous les paiements" />
          <Tab label="Aujourd'hui" />
          <Tab label="Ce mois" />
          <Tab label="En attente" />
        </Tabs>
      </Paper>

      {/* Barre de recherche et filtres */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Rechercher par client, numéro reçu..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
              }}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Statut</InputLabel>
              <Select
                value={statusFilter}
                label="Statut"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                {paymentStatuses.map(status => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Méthode</InputLabel>
              <Select
                value={paymentMethodFilter}
                label="Méthode"
                onChange={(e) => setPaymentMethodFilter(e.target.value)}
              >
                <MenuItem value="all">Toutes</MenuItem>
                {paymentMethods.map(method => (
                  <MenuItem key={method.value} value={method.value}>
                    {method.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchData}
            >
              Actualiser
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tableau */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'success.light' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Numéro</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Client</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Abonnement</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Montants</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Méthode</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Statut</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Chargement...
                </TableCell>
              </TableRow>
            ) : !Array.isArray(filteredPayments) || filteredPayments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Box py={3}>
                    <Typography variant="body1" color="textSecondary" gutterBottom>
                      Aucun paiement trouvé
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total dans payments state: {payments.length}
                    </Typography>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      onClick={fetchData}
                      sx={{ mt: 1 }}
                    >
                      Recharger
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredPayments.map((payment) => {
                if (!payment) return null;
                
                const statusInfo = getStatusInfo(payment.status);
                const clientName = getClientName(payment);
                const membershipType = getMembershipType(payment);
                
                return (
                  <TableRow key={payment.id} hover>
                    <TableCell>
                      <Chip 
                        label={payment.receipt_number || 'N/A'} 
                        color="primary" 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Person color="action" />
                        <Typography>
                          {clientName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {membershipType}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexDirection="column">
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="body2" fontWeight="bold">
                            {payment.amount_htg?.toLocaleString() || 0} G
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="body2" color="text.secondary">
                            {payment.amount_usd || 0} $
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {getPaymentMethodIcon(payment.payment_method)}
                        <Typography variant="body2">
                          {getPaymentMethodLabel(payment.payment_method)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(payment.payment_date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusInfo.label}
                        color={statusInfo.color}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Imprimer reçu">
                          <IconButton 
                            color="info" 
                            size="small"
                            onClick={() => {
                              // Fonction d'impression simplifiée
                              const printContent = `
                                <h2>Reçu de Paiement</h2>
                                <p><strong>Client:</strong> ${clientName}</p>
                                <p><strong>Montant HTG:</strong> ${payment.amount_htg} G</p>
                                <p><strong>Montant USD:</strong> ${payment.amount_usd} $</p>
                                <p><strong>Date:</strong> ${formatDate(payment.payment_date)}</p>
                              `;
                              const printWindow = window.open('', '_blank');
                              printWindow.document.write(printContent);
                              printWindow.document.close();
                              printWindow.print();
                            }}
                          >
                            <Print />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Modifier">
                          <IconButton 
                            color="primary" 
                            size="small"
                            onClick={() => handleOpenDialog(payment)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton 
                            color="error" 
                            size="small"
                            onClick={() => handleDelete(payment.id)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog pour créer/modifier */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPayment ? '✏️ Modifier Paiement' : '➕ Nouveau Paiement'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Client</InputLabel>
                  <Select
                    value={formData.client}
                    label="Client"
                    onChange={(e) => setFormData({...formData, client: e.target.value})}
                    error={!formData.client}
                  >
                    <MenuItem value="">
                    </MenuItem>
                    {Array.isArray(clients) && clients.map(client => (
                      <MenuItem key={client.id} value={client.id}>
                        {client.first_name} {client.last_name} ({client.code || client.id})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Abonnement (optionnel)</InputLabel>
                  <Select
                    value={formData.membership}
                    label="Abonnement (optionnel)"
                    onChange={(e) => {
                      const selectedMembership = memberships.find(m => m.id === e.target.value);
                      setFormData({
                        ...formData,
                        membership: e.target.value,
                        amount_usd: selectedMembership?.price_usd || formData.amount_usd,
                        amount_htg: selectedMembership?.price_htg || formData.amount_htg
                      });
                    }}
                  >
                    <MenuItem value="">
                    </MenuItem>
                    {Array.isArray(memberships) && memberships
                      .filter(m => m.status === 'ACTIVE')
                      .map(membership => (
                        <MenuItem key={membership.id} value={membership.id}>
                          {membership.membership_type} - {membership.price_usd}$ / {membership.price_htg} G
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Montant USD ($)"
                  type="number"
                  value={formData.amount_usd}
                  onChange={(e) => setFormData({...formData, amount_usd: e.target.value})}
                  margin="normal"
                  InputProps={{
                    startAdornment: <AttachMoney sx={{ mr: 1 }} />,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Montant HTG (G)*"
                  type="number"
                  value={formData.amount_htg}
                  onChange={(e) => setFormData({...formData, amount_htg: e.target.value})}
                  required
                  margin="normal"
                  error={!formData.amount_htg}
                  helperText="Champ obligatoire"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Numéro de reçu"
                  value={formData.receipt_number}
                  onChange={(e) => setFormData({...formData, receipt_number: e.target.value})}
                  margin="normal"
                  helperText="Généré automatiquement si vide"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Méthode de paiement</InputLabel>
                  <Select
                    value={formData.payment_method}
                    label="Méthode de paiement"
                    onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                  >
                    {paymentMethods.map(method => (
                      <MenuItem key={method.value} value={method.value}>
                        {method.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={formData.status}
                    label="Statut"
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                  >
                    {paymentStatuses.map(status => (
                      <MenuItem key={status.value} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date de paiement"
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                  required
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  multiline
                  rows={2}
                  margin="normal"
                  placeholder="Notes supplémentaires..."
                />
              </Grid>
              
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Important:</strong> 
                  </Typography>
                  <Typography variant="body2">
                    1. Le montant HTG est obligatoire
                  </Typography>
                  <Typography variant="body2">
                    2. Les méthodes de paiement doivent être en majuscules (CASH, CARD, etc.)
                  </Typography>
                  <Typography variant="body2">
                    3. Les statuts doivent être en majuscules (PAID, PENDING, etc.)
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annuler</Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={!formData.client || !formData.amount_htg}
            >
              {editingPayment ? 'Modifier' : 'Créer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GymPayments;